import {ReactPlugin} from "@microsoft/applicationinsights-react-js";
import {ApplicationInsights, type ITelemetryItem} from '@microsoft/applicationinsights-web';

/**
 * Trace context for distributed tracing, following W3C Trace Context specification.
 * @see https://www.w3.org/TR/trace-context/
 */
export interface TraceContext {
    /** The trace ID (32 hex characters) - identifies the entire distributed trace */
    traceId: string;
    /** The span/parent ID (16 hex characters) - identifies this specific operation */
    spanId: string;
    /** W3C traceparent header value in format: 00-{traceId}-{spanId}-01 */
    traceparent: string;
}

let appInsightsInstance: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;
let pageViewContextProvider: (() => Record<string, string>) | null = null;

/**
 * Registers a function that supplies extra properties to attach to every
 * PageView (and PageViewPerformance) telemetry envelope. Called at send time
 * so the values reflect current state, not the state at registration time.
 *
 * The provider is held as a callback rather than imported directly to avoid a
 * circular dependency between this module and the store module that sources
 * the values.
 */
export function setPageViewContextProvider(provider: () => Record<string, string>): void {
    pageViewContextProvider = provider;
}

export function initializeAppInsights(connectionString: string): ApplicationInsights {
    if (appInsightsInstance) {
        return appInsightsInstance;
    }

    reactPlugin = new ReactPlugin();

    appInsightsInstance = new ApplicationInsights({
        config: {
            connectionString: connectionString,
            extensions: [reactPlugin],
            enableAutoRouteTracking: true,
            disableExceptionTracking: false,
            autoTrackPageVisitTime: true,
            // Disable fetch tracking to prevent conflicts with Next.js's patched window.fetch
            // Next.js patches fetch for caching/revalidation which breaks AI SDK's fetch instrumentation
            // See: https://github.com/microsoft/ApplicationInsights-JS/issues/2145
            disableFetchTracking: true,
            // Disable CORS correlation to prevent adding request-id/traceparent headers to external APIs
            // These headers cause CORS errors with third-party APIs like Avatar SDK
            enableCorsCorrelation: false,
            enableRequestHeaderTracking: false,
            enableResponseHeaderTracking: false,
            disableCookiesUsage: true,
            // Unlimited: the default (500) pauses ALL dependency tracking until the next
            // trackPageView(). A page that polls and never navigates — the postshow wall —
            // burns the budget and then reports nothing for the rest of its multi-day life.
            maxAjaxCallsPerView: -1,
            enableUnhandledPromiseRejectionTracking: true
        }
    });

    appInsightsInstance.loadAppInsights();
    appInsightsInstance.addTelemetryInitializer(rewriteManualDependencyIds);
    appInsightsInstance.addTelemetryInitializer(attachPageViewUserContext);
    setupGlobalErrorHandlers();

    return appInsightsInstance;
}

/**
 * The AI JS SDK stamps dependency telemetry with the SDK's *current*
 * `telemetryTrace.traceID` and rewrites the supplied id into its hierarchical
 * `|trace.span.` shape — neither matches the W3C traceparent we put on the wire
 * inside `traceDependency`, so server-side request rows can't join back. We
 * mark our manual dependencies with `_traceId` / `_spanId` properties and this
 * initializer rewrites the envelope to the W3C ids before send.
 */
function rewriteManualDependencyIds(envelope: ITelemetryItem): boolean | void {
    if (envelope.baseType !== 'RemoteDependencyData') return;

    const baseData = envelope.baseData as
        | {id?: string; properties?: Record<string, unknown>}
        | undefined;
    const props = baseData?.properties;
    if (!props) return;
    const traceId = props['_traceId'];
    const spanId = props['_spanId'];
    if (typeof traceId !== 'string' || typeof spanId !== 'string') return;

    envelope.tags = envelope.tags ?? {};
    envelope.tags['ai.operation.id'] = traceId;
    // Pageview is the conceptual parent; for a root pageview the AI SDK uses
    // `pageview.id == operation_Id`, so the dep's parent id is the trace id too.
    envelope.tags['ai.operation.parentId'] = traceId;
    baseData!.id = spanId;

    delete props['_traceId'];
    delete props['_spanId'];
}

function attachPageViewUserContext(envelope: ITelemetryItem): boolean | void {
    if (envelope.baseType !== 'PageviewData' && envelope.baseType !== 'PageviewPerformanceData') return;
    if (!pageViewContextProvider) return;

    const baseData = envelope.baseData as { properties?: Record<string, unknown> } | undefined;
    if (!baseData) return;

    const extra = pageViewContextProvider();
    baseData.properties = { ...baseData.properties, ...extra };
}

export function getAppInsights(): ApplicationInsights | null {
    return appInsightsInstance;
}

function setupGlobalErrorHandlers(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('error', (event: ErrorEvent) => {
        if (appInsightsInstance) {
            appInsightsInstance.trackException({
                exception: event.error || new Error(event.message),
                severityLevel: 3,
                properties: {
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                    type: 'window.error'
                }
            });
        }
    });

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        if (appInsightsInstance) {
            const error = event.reason instanceof Error
                ? event.reason
                : new Error(String(event.reason));

            appInsightsInstance.trackException({
                exception: error,
                severityLevel: 3,
                properties: {
                    type: 'unhandledrejection',
                    reason: String(event.reason)
                }
            });
        }
    });
}

/**
 * Generates a random 16-character hex string for use as a span ID.
 */
function generateSpanId(): string {
    const array = new Uint8Array(8);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Gets the current trace context from Application Insights, creating a new span ID for this operation.
 */
function createTraceContext(): TraceContext {
    const traceId = appInsightsInstance?.context?.telemetryTrace?.traceID
        ?? crypto.randomUUID().replace(/-/g, '');
    const spanId = generateSpanId();
    const traceparent = `00-${traceId}-${spanId}-01`;

    return {traceId, spanId, traceparent};
}

/** Supported dependency types for Application Insights tracking */
export type DependencyType = 'HTTP' | 'SQL' | 'Azure Service Bus' | 'InProc' | 'Queue Message' | 'gRPC';

/**
 * OpenTelemetry-compatible SpanKind for categorizing trace spans.
 * @see https://opentelemetry.io/docs/concepts/signals/traces/#span-kind
 */
export enum SpanKind {
    /** Default. Internal operation within the application */
    INTERNAL = 'INTERNAL',
    /** Handling a request from an external source (server-side) */
    SERVER = 'SERVER',
    /** Making a request to an external service */
    CLIENT = 'CLIENT',
    /** Sending a message to a broker/queue */
    PRODUCER = 'PRODUCER',
    /** Receiving a message from a broker/queue */
    CONSUMER = 'CONSUMER',
}

export interface TracedOptions {
    /** The kind of span (defaults to INTERNAL) */
    kind?: SpanKind;
    /** Custom attributes to attach to the span */
    attributes?: Record<string, string | number | boolean>;
    /** Target for CLIENT/PRODUCER spans (e.g., host, queue name) */
    target?: string;
}

/**
 * OpenTelemetry-style tracing wrapper.
 * Traces an operation with the given name and options, recording duration and success/failure.
 *
 * @param name - Span name (e.g., "processOrder", "GET /api/user")
 * @param options - Tracing options (kind, attributes, target)
 * @param fn - The operation to trace (receives trace context)
 * @returns The result of the operation
 *
 * @example
 * ```typescript
 * // Internal operation (default kind)
 * const result = await traced('processUserData', {}, async (ctx) => {
 *   return { status: 200, data: processed };
 * });
 *
 * // HTTP client call
 * const user = await traced(
 *   'GET /api/user',
 *   { kind: SpanKind.CLIENT, target: 'api.example.com' },
 *   async (ctx) => {
 *     const response = await fetch('/api/user', {
 *       headers: { traceparent: ctx.traceparent }
 *     });
 *     return { status: response.status, data: await response.json() };
 *   }
 * );
 *
 * // Message producer
 * await traced(
 *   'SendOrderMessage',
 *   { kind: SpanKind.PRODUCER, target: 'orders-topic', attributes: { 'messaging.system': 'servicebus' } },
 *   async (ctx) => {
 *     await sendMessage(order, ctx.traceparent);
 *     return { status: 200 };
 *   }
 * );
 * ```
 */
export async function traced<T extends { status?: number }>(
    name: string,
    options: TracedOptions,
    fn: (context: TraceContext) => Promise<T>
): Promise<T> {
    const {kind = SpanKind.INTERNAL, attributes = {}, target} = options;

    // SERVER kind is not supported in browser context
    if (kind === SpanKind.SERVER) {
        throw new Error('SpanKind.SERVER is not supported in browser context. Use server-side tracing for incoming requests.');
    }

    const dependencyType = mapKindToDependencyType(kind, attributes);

    return traceDependency(
        {
            name,
            target: target ?? name,
            type: dependencyType,
            attributes: {...attributes, 'span.kind': kind},
        },
        fn
    );
}

/**
 * Maps SpanKind and attributes to Application Insights dependency type.
 */
function mapKindToDependencyType(
    kind: SpanKind,
    attributes: Record<string, string | number | boolean>
): DependencyType {
    // INTERNAL operations are in-process
    if (kind === SpanKind.INTERNAL) {
        return 'InProc';
    }

    // Check for messaging system attribute (for PRODUCER/CONSUMER)
    const messagingSystem = attributes['messaging.system'];
    if (messagingSystem === 'servicebus' || messagingSystem === 'azure_servicebus') {
        return 'Azure Service Bus';
    }
    if (messagingSystem) {
        return 'Queue Message';
    }

    // Default for PRODUCER/CONSUMER without messaging.system attribute
    if (kind === SpanKind.PRODUCER || kind === SpanKind.CONSUMER) {
        return 'Queue Message';
    }

    // Check for database attribute
    const dbSystem = attributes['db.system'];
    if (dbSystem) {
        return 'SQL';
    }

    // Check for RPC system
    const rpcSystem = attributes['rpc.system'];
    if (rpcSystem === 'grpc') {
        return 'gRPC';
    }

    // Default for CLIENT
    return 'HTTP';
}

/**
 * A long-lived span handle. Unlike `traced` / `traceDependency`, the span's
 * lifetime is not bound to a single async operation — call `.end()` explicitly
 * when the conceptual work finishes. Attributes can be added between start and
 * end; the final snapshot is sent as the dependency's properties.
 */
export interface Span {
    setAttribute(key: string, value: string | number | boolean): void;
    setAttributes(attrs: Record<string, string | number | boolean>): void;
    end(options?: {status?: number; error?: unknown}): void;
}

export interface StartSpanOptions {
    /** The kind of span (defaults to INTERNAL → InProc dependency) */
    kind?: SpanKind;
    /** Target for CLIENT/PRODUCER spans (defaults to the span name) */
    target?: string;
    /** Initial attributes to attach to the span */
    attributes?: Record<string, string | number | boolean>;
}

/**
 * Starts a span whose lifetime is managed manually. Use for work that spans
 * multiple async boundaries or React renders (e.g., "this cohort was on screen
 * from T₀ to T₁"). For request-scoped work, prefer `traced` / `traceDependency`.
 *
 * @example
 * ```typescript
 * const span = startSpan('CohortVisible', {
 *   kind: SpanKind.INTERNAL,
 *   attributes: {cohortId: '…'},
 * });
 * // …later, possibly across many renders…
 * span.setAttribute('finalUserCount', users.length);
 * span.end();
 * ```
 */
export function startSpan(name: string, options: StartSpanOptions = {}): Span {
    const {kind = SpanKind.INTERNAL, target, attributes = {}} = options;

    if (kind === SpanKind.SERVER) {
        throw new Error('SpanKind.SERVER is not supported in browser context.');
    }

    const dependencyType = mapKindToDependencyType(kind, attributes);
    const context = createTraceContext();
    const startTime = performance.now();
    let ended = false;

    const accumulated: Record<string, string | number | boolean> = {
        ...attributes,
        'span.kind': kind,
        traceId: context.traceId,
        spanId: context.spanId,
        _traceId: context.traceId,
        _spanId: context.spanId,
    };

    return {
        setAttribute(key, value) {
            if (ended) return;
            accumulated[key] = value;
        },
        setAttributes(attrs) {
            if (ended) return;
            Object.assign(accumulated, attrs);
        },
        end(endOptions) {
            if (ended) return;
            ended = true;
            const duration = performance.now() - startTime;
            const error = endOptions?.error;
            const responseCode = endOptions?.status ?? (error ? 0 : 200);
            const success = !error && responseCode >= 200 && responseCode < 400;

            if (error) {
                accumulated.error = error instanceof Error ? error.message : String(error);
            }

            appInsightsInstance?.trackDependencyData({
                id: context.spanId,
                name,
                duration,
                success,
                responseCode,
                type: dependencyType,
                target: target ?? name,
                properties: accumulated as Record<string, string>,
            });
        },
    };
}

export interface DependencyOptions {
    /** Name of the dependency (e.g., "GET /api/user", "SendMessage") */
    name: string;
    /** Target URL or service name (e.g., "api.example.com", "my-service-bus") */
    target: string;
    /** Type of dependency (defaults to 'HTTP') */
    type?: DependencyType;
    /** Custom attributes to attach to the span */
    attributes?: Record<string, string | number | boolean>;
}

/**
 * Tracks an external dependency call with distributed tracing.
 * Automatically injects trace context and records duration, success/failure, and response codes.
 *
 * @param options - Dependency tracking options (name, target, type)
 * @param operation - Callback that receives trace context and performs the call
 * @returns The result of the operation
 *
 * @example
 * ```typescript
 * // HTTP call
 * const user = await traceDependency(
 *   { name: 'GET /api/user', target: 'api.example.com' },
 *   async (ctx) => {
 *     const response = await fetch('https://api.example.com/user', {
 *       headers: { traceparent: ctx.traceparent }
 *     });
 *     return { data: await response.json(), status: response.status };
 *   }
 * );
 *
 * // Service Bus message
 * await traceDependency(
 *   { name: 'SendMessage', target: 'my-topic', type: 'Azure Service Bus' },
 *   async (ctx) => {
 *     await sendToServiceBus(message, ctx.traceparent);
 *     return { status: 200 };
 *   }
 * );
 * ```
 */
export async function traceDependency<T extends { status?: number }>(
    options: DependencyOptions,
    operation: (context: TraceContext) => Promise<T>
): Promise<T> {
    const {name, target, type = 'HTTP', attributes = {}} = options;
    const context = createTraceContext();
    const startTime = performance.now();

    const allAttributes: Record<string, string | number | boolean> = {
        ...attributes,
        traceId: context.traceId,
        spanId: context.spanId,
        // Markers picked up by `rewriteManualDependencyIds` to set
        // ai.operation.id / dep.id from the W3C traceparent we sent on the
        // wire (rather than the SDK's current pageview traceID).
        _traceId: context.traceId,
        _spanId: context.spanId,
    };

    try {
        const result = await operation(context);
        const duration = performance.now() - startTime;
        const responseCode = result.status ?? 200;
        const success = responseCode >= 200 && responseCode < 400;

        appInsightsInstance?.trackDependencyData({
            id: context.spanId,
            name,
            duration,
            success,
            responseCode,
            type,
            target,
            properties: allAttributes as Record<string, string>,
        });

        return result;
    } catch (error) {
        const duration = performance.now() - startTime;

        appInsightsInstance?.trackDependencyData({
            id: context.spanId,
            name,
            duration,
            success: false,
            responseCode: 0,
            type,
            target,
            properties: {
                ...allAttributes,
                error: error instanceof Error ? error.message : String(error),
            } as Record<string, string>,
        });

        throw error;
    }
}

